import { DialogAwareContentContainer, DialogContent, Typography, Button } from "@/components/shared";
import { PostHogEvent } from "@/posthog/posthog-capture-names";
import { PosthogHelper } from "@/posthog/posthog-helper";
import { useCallback, useMemo, useState } from "react";
import { EXIT_LOCK_CONTACT } from "../exit-lock-whale-retain.config";
import { BREAKPOINTS } from "@/config/breakpoints.config";
import { useDialogPortal } from "@/hooks/shared/dialog-portal/useDialogPortal";
import type { ExitLockCapturedParams } from "../exit-lock-capture-params.type";
import type { WithdrawLockTransactionProps } from "@/components/transaction";

type ExitLockWhaleRetainDialogProps = {
    exitLockCaptureParams: ExitLockCapturedParams;
};

export const ExitLockWhaleRetainDialog = ({ exitLockCaptureParams }: ExitLockWhaleRetainDialogProps) => {
    const [clickedContactVal, setClickedContactVal] = useState(false);

    const { openDialog } = useDialogPortal();

    const contactVariant = useMemo(() => (clickedContactVal ? "secondary" : "primary"), [clickedContactVal]);
    const continueVariant = useMemo(() => (clickedContactVal ? "primary" : "secondary"), [clickedContactVal]);

    const handleOpenExitLockContact = useCallback(() => {
        try {
            setClickedContactVal(true);
            PosthogHelper.capture({
                event_name: PostHogEvent.ExitLockWhaleDialog.ContactButton.clicked,
                properties: exitLockCaptureParams,
                allowTemporaryOptIn: false,
                userIdentifier: exitLockCaptureParams.userAddress,
            });
        } catch (error) {
            console.error("Error capturing posthog event:", error);
        } finally {
            window.open(EXIT_LOCK_CONTACT.href, "_blank");
        }
    }, [exitLockCaptureParams, clickedContactVal]);

    const handleConfirmExit = useCallback(() => {
        try {
            const event_name = clickedContactVal
                ? PostHogEvent.ExitLockWhaleDialog.ContinueButton.clickedAfterClickingContact
                : PostHogEvent.ExitLockWhaleDialog.ContinueButton.clickedToSkip;
            PosthogHelper.capture({
                event_name,
                properties: exitLockCaptureParams,
                allowTemporaryOptIn: false,
                userIdentifier: exitLockCaptureParams.userAddress,
            });
        } catch (error) {
            console.error("Error capturing posthog event:", error);
        } finally {
            openDialog({
                dialogType: "WITHDRAW_LOCK_CONFIRM",
                dialogProps: {
                    withdrawLockAmount: exitLockCaptureParams.exitSize,
                    lockDuration: exitLockCaptureParams.lockDuration,
                } satisfies WithdrawLockTransactionProps,
            });
        }
    }, [exitLockCaptureParams, clickedContactVal]);

    return (
        <DialogContent
            style={{
                maxWidth: BREAKPOINTS.sm,
                minWidth: BREAKPOINTS.xs,
                // width: BREAKPOINTS.sm,
            }}
        >
            <DialogAwareContentContainer isDialog>
                <Typography className="my-4 text-dark">
                    Given the size of your position, we'd love to better understand your plans and hear your feedback
                    before you proceed.
                </Typography>
                <Typography className="my-4 text-dark">
                    Reach out to {EXIT_LOCK_CONTACT.name} ({EXIT_LOCK_CONTACT.title}) if you'd like to discuss your
                    position, clarify any product questions, or explore the best next steps for your funds.
                </Typography>
                <div className="grid sm:grid-cols-2 sm:gap-6 mb-4">
                    <Button variant={contactVariant} onClick={handleOpenExitLockContact} fullWidth>
                        Contact {EXIT_LOCK_CONTACT.name}
                    </Button>
                    <Button variant={continueVariant} onClick={handleConfirmExit} fullWidth>
                        Confirm Exit
                    </Button>
                </div>
            </DialogAwareContentContainer>
        </DialogContent>
    );
};
